import { useEffect, useState } from 'react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '#app/components/ui'
import screenshotCalendar from '#app/images/screenshots/calendar.png'
import screenshotTeam from '#app/images/screenshots/team.png'
import screenshotTimeline from '#app/images/screenshots/timeline.png'
import screenshotVendors from '#app/images/screenshots/vendors.png'
import { cn } from '#app/utils/misc.tsx'

const features = [
	{
		title: 'Team Members',
		description:
			'Effortlessly manage every aspect of your event by seamlessly integrating team members into the planning process.',
		image: screenshotTeam,
	},
	{
		title: 'Vendors',
		description:
			'Seamlessly manage vendor relationships by centralizing communication, contracts, and deliverables all in one place.',
		image: screenshotVendors,
	},
	{
		title: 'Event Timelines',
		description:
			'Create, customize, and visualize your event timeline with a user-friendly interface that lets you easily add milestones, tasks, and deadlines.',
		image: screenshotTimeline,
	},
	{
		title: 'Calendar',
		description:
			'Seamlessly integrate your event timeline with your personal or team calendar, giving you a holistic view of your commitments and tasks.',
		image: screenshotCalendar,
	},
]

export function PrimaryFeaturesTabs() {
	let [tabOrientation, setTabOrientation] = useState('horizontal')

	useEffect(() => {
		let lgMediaQuery = window.matchMedia('(min-width: 1024px)')

		function onMediaQueryChange({ matches }: { matches: boolean }) {
			setTabOrientation(matches ? 'vertical' : 'horizontal')
		}

		onMediaQueryChange(lgMediaQuery)
		lgMediaQuery.addEventListener('change', onMediaQueryChange)

		return () => {
			lgMediaQuery.removeEventListener('change', onMediaQueryChange)
		}
	}, [])

	// TODO: Style this component using data-[state=active]

	return (
		<Tabs
			defaultValue="Team Members"
			className="mt-16 grid grid-cols-1 items-start gap-y-2 pt-10 sm:gap-y-6 md:mt-20 lg:grid-cols-12 lg:pt-0"
			orientation={tabOrientation === 'vertical' ? 'vertical' : 'horizontal'}
		>
			<div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
				<TabsList className="whitespace-nowrappx-4 relative z-10 flex gap-x-4 bg-inherit dark:bg-inherit sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
					{features.map(feature => (
						<div
							key={feature.title}
							className={cn(
								'group relative px-4 py-1 lg:rounded-l-xl lg:rounded-r-none lg:p-6',
								'lg:hover:bg-white/5',
								'rounded-none sm:rounded-lg',
							)}
						>
							<h3>
								<TabsTrigger
									value={feature.title}
									className={cn('font-display text-lg text-white lg:-ml-4')}
								>
									<span className="absolute inset-0 rounded-full lg:rounded-l-xl lg:rounded-r-none" />
									{feature.title}
								</TabsTrigger>
							</h3>
							<p
								className={cn(
									'mt-2 hidden text-sm lg:block',
									'group-hover:text-white lg:text-white',
								)}
							>
								{feature.description}
							</p>
						</div>
					))}
				</TabsList>
			</div>
			<div className="lg:col-span-7">
				{features.map(feature => (
					<TabsContent key={feature.title} value={feature.title}>
						<div className="relative sm:px-6 lg:hidden">
							<div className="absolute -inset-x-4 bottom-[-4.25rem] top-[-6.5rem] bg-white/10 ring-1 ring-inset ring-white/10 sm:inset-x-0 sm:rounded-t-xl" />
							<p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
								{feature.description}
							</p>
						</div>
						<div className="mt-10 w-[45rem] overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-emerald-900/20 sm:w-auto lg:mt-0 lg:w-[67.8125rem]">
							<link rel="preload" href={screenshotCalendar} as="image" />
							<link rel="preload" href={screenshotTeam} as="image" />
							<link rel="preload" href={screenshotTimeline} as="image" />
							<link rel="preload" href={screenshotVendors} as="image" />
							<img
								className="w-full"
								src={feature.image}
								alt=""
								loading="lazy"
								sizes="(min-width: 1024px) 67.8125rem, (min-width: 640px) 100vw, 45rem"
							/>
						</div>
					</TabsContent>
				))}
			</div>
		</Tabs>
	)
}
